import { useEffect, useRef, useReducer } from "react";
import cx from "classnames";
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from "body-scroll-lock";

import { useAuth } from "utils/auth";
import ExpandedNav from "./ExpandedNav";
import Search from "./Search";
import Hamburger from "./Hamburger";
import Logo from "assets/atlantic-logo.svg";
import BigA from "assets/big-a.svg";
import SearchIcon from "assets/search-icon.svg";
import useMedia from "utils/useMedia";

import styles from "./Nav.module.scss";

type NavState = {
  navIsExpanded: boolean;
  searchIsExpanded: boolean;
  overlayOpen: boolean;
};

type NavAction =
  | { type: "OPEN_EXPANDED_NAV" }
  | { type: "CLOSE_EXPANDED_NAV" }
  | { type: "OPEN_DESKTOP_SEARCH" }
  | { type: "CLOSE_DESKTOP_SEARCH" }
  | { type: "OPEN_MOBILE_SEARCH" }
  | { type: "CLOSE_MOBILE_SEARCH" };

/**
 * Instead of directly updating nav state, we use a reducer to dispatch
 * different types of actions that put the nav in a discrete state. This
 * helps prevent unwanted and inconsistent nav states.
 */
const navReducer = (state: NavState, action: NavAction) => {
  switch (action.type) {
    case "OPEN_EXPANDED_NAV":
      return {
        navIsExpanded: true,
        searchIsExpanded: false,
        overlayOpen: true,
      };
    case "CLOSE_EXPANDED_NAV":
      return {
        navIsExpanded: false,
        searchIsExpanded: false,
        overlayOpen: false,
      };
    case "OPEN_DESKTOP_SEARCH":
      return {
        navIsExpanded: false,
        searchIsExpanded: true,
        overlayOpen: true,
      };
    case "CLOSE_DESKTOP_SEARCH":
      return {
        navIsExpanded: false,
        searchIsExpanded: false,
        overlayOpen: false,
      };
    case "OPEN_MOBILE_SEARCH":
      return {
        navIsExpanded: true,
        searchIsExpanded: true,
        overlayOpen: true,
      };
    case "CLOSE_MOBILE_SEARCH":
      return {
        navIsExpanded: true,
        searchIsExpanded: false,
        overlayOpen: true,
      };
  }
};

const Nav = () => {
  const { isLoggedIn, hasPaywallAccess } = useAuth();

  const [isMobile, isDesktop] = useMedia(
    "(max-width: 975px)",
    "(min-width: 976px)"
  );

  const [
    { navIsExpanded, searchIsExpanded, overlayOpen },
    dispatch,
  ] = useReducer(navReducer, {
    navIsExpanded: false,
    searchIsExpanded: false,
    overlayOpen: false,
  });

  const navRef = useRef<HTMLDivElement>(null!);
  const expandedNavRef = useRef<HTMLDivElement>(null!);
  const desktopSearchRef = useRef<HTMLDivElement>(null!);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null!);
  const searchButtonRef = useRef<HTMLButtonElement>(null!);

  const openNav = () => dispatch({ type: "OPEN_EXPANDED_NAV" });
  const closeNav = () => dispatch({ type: "CLOSE_EXPANDED_NAV" });
  const openDesktopSearch = () => dispatch({ type: "OPEN_DESKTOP_SEARCH" });
  const closeDesktopSearch = () => dispatch({ type: "CLOSE_DESKTOP_SEARCH" });
  const openMobileSearch = () => dispatch({ type: "OPEN_MOBILE_SEARCH" });
  const closeMobileSearch = () => dispatch({ type: "CLOSE_MOBILE_SEARCH" });

  /**
   * Disable body scrolling when the nav is open, and restore
   * scroll to the original scroll position when the nav is
   * closed or the component unmounts. The `body-scroll-lock`
   * library is a better than just adding `overflow: hidden` to
   * the`body` element because it handles browser and mobile
   * inconsistencies
   *
   * See the following for more context:
   * https://github.com/willmcpo/body-scroll-lock
   * https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
   */
  useEffect(() => {
    if (overlayOpen) {
      disableBodyScroll(navRef.current);
    } else {
      enableBodyScroll(navRef.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [overlayOpen]);

  /**
   * Close the expanded nav and search box when a user clicks on the Escape key
   */
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const activeElement = document.activeElement;

        if (navIsExpanded && !searchIsExpanded) {
          closeNav();
          /**
           * if focus is inside of the expanded nav, we want to
           * return focus back to the hamburger button on Escape
           */
          if (expandedNavRef.current.contains(activeElement)) {
            hamburgerButtonRef.current.focus({ preventScroll: true });
          }
        }

        if (searchIsExpanded && isDesktop) {
          closeDesktopSearch();
          /**
           * if focus is inside of the search box, we want to
           * return focus back to the search button on Escape
           */
          if (desktopSearchRef.current.contains(activeElement)) {
            searchButtonRef.current.focus({ preventScroll: true });
          }
        }

        if (searchIsExpanded && isMobile) {
          /**
           * On mobile, the react-focus-lock package handles bringing focus back to the
           * button that triggered it, so we don't need custom logic to do it on Escape
           */
          closeMobileSearch();
        }
      }
    };

    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [navIsExpanded, searchIsExpanded, isDesktop, isMobile]);

  return (
    <>
      <nav
        ref={navRef}
        className={cx(styles.root, navIsExpanded && styles.isExpanded)}
        aria-labelledby="site-navigation"
        data-category="Site Nav"
      >
        <a href="#main-content" className={styles.skipLink}>
          Skip to content
        </a>
        <h2 id="site-navigation" className={styles.visuallyHide}>
          Site Navigation
        </h2>
        <div className={styles.flexContainer}>
          <ul className={styles.leftContainer}>
            <li className={cx(styles.navListItem, styles.visuallyHideOnMobile)}>
              <a href="https://www.theatlantic.com" className={styles.navLink}>
                <BigA title="The Atlantic" className={styles.bigA} />
              </a>
            </li>
            <li className={cx(styles.navListItem, styles.hamburgerLi)}>
              <button
                className={styles.buttonReset}
                onClick={() => (navIsExpanded ? closeNav() : openNav())}
                aria-expanded={navIsExpanded}
                aria-controls="expanded-nav"
                aria-label={
                  navIsExpanded ? "Close Main Menu" : "Open Main Menu"
                }
                data-action={navIsExpanded ? "Close Menu" : "Open Menu"}
                ref={hamburgerButtonRef}
              >
                <Hamburger isExpanded={navIsExpanded} />
              </button>
              <ExpandedNav
                navIsExpanded={navIsExpanded}
                searchIsExpanded={searchIsExpanded}
                openMobileSearch={openMobileSearch}
                closeMobileSearch={closeMobileSearch}
                ref={expandedNavRef}
                isMobile={isMobile}
                id="expanded-nav"
              />
            </li>
            <li
              className={cx(
                styles.navListItem,
                styles.hideOnTablet,
                styles.searchLi,
                searchIsExpanded && styles.bigBoi
              )}
            >
              <button
                className={styles.buttonReset}
                ref={searchButtonRef}
                aria-label={
                  searchIsExpanded ? "Close Search" : "Search The Atlantic"
                }
                aria-expanded={searchIsExpanded}
                aria-controls="nav-desktop-search"
                onClick={
                  searchIsExpanded ? closeDesktopSearch : openDesktopSearch
                }
              >
                <SearchIcon
                  className={styles.searchIcon}
                  data-action={
                    searchIsExpanded ? "Close Search" : "Open Search"
                  }
                />
              </button>
              <Search
                searchIsExpanded={searchIsExpanded && isDesktop}
                closeSearch={() => {
                  closeDesktopSearch();
                  searchButtonRef.current.focus({ preventScroll: true });
                }}
                id="nav-desktop-search"
                ref={desktopSearchRef}
              />
            </li>
            <li
              hidden={searchIsExpanded}
              className={cx(
                styles.navListItem,
                styles.hideOnTablet,
                searchIsExpanded && styles.displayNone
              )}
            >
              <a
                className={styles.navLink}
                href="https://www.theatlantic.com/most-popular/"
                data-action="popular"
              >
                Popular
              </a>
            </li>
            <li
              className={cx(
                styles.navListItem,
                styles.hideOnTablet,
                searchIsExpanded && styles.displayNone
              )}
            >
              <a
                className={styles.navLink}
                href="https://www.theatlantic.com/latest/"
                data-action="latest"
              >
                Latest
              </a>
            </li>
          </ul>
          {/**
           * Hide the full logo for assistive tech because the big A logo
           * is better placed semantically for a "Home" link, and the full logo
           * link is duplicative content.
           * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-hidden_attribute
           *
           * Since both logos are hidden, we need to make them unfocusable
           * in the tab order.
           * See: https://dequeuniversity.com/rules/axe/3.3/aria-hidden-focus
           */}
          <div aria-hidden={true} className={styles.middleContainer}>
            <a
              href="https://www.theatlantic.com"
              className={cx(styles.hideAboveMobile, styles.mobileBigALink)}
              tabIndex={-1}
              data-action="big a"
            >
              <BigA className={styles.mobileBigA} />
            </a>
            <a
              href="https://www.theatlantic.com"
              className={styles.navLink}
              tabIndex={-1}
              data-action="logo"
            >
              <Logo className={styles.logo} />
            </a>
          </div>
          <ul className={styles.rightContainer}>
            <li className={styles.navListItem}>
              <a
                data-action={isLoggedIn ? "Open Account" : "Sign In"}
                href={
                  isLoggedIn
                    ? "https://accounts.theatlantic.com/accounts/details/"
                    : "https://accounts.theatlantic.com/login/"
                }
                className={cx(
                  styles.navLink,
                  // Only hide the sign in link on mobile if the nav isn't expanded
                  !navIsExpanded && styles.hideOnMobile
                )}
              >
                {isLoggedIn ? "My Account" : "Sign In"}
              </a>
            </li>
            <li className={styles.navListItem}>
              <a
                data-action={hasPaywallAccess ? "Give a Gift" : "Subscribe"}
                href={
                  hasPaywallAccess
                    ? "https://accounts.theatlantic.com/products/gift/"
                    : "https://www.theatlantic.com/subscribe/navbar/"
                }
                className={styles.subscribe}
                onBlur={(event: React.FocusEvent<HTMLAnchorElement>) => {
                  // If the user tabs back into the nav, we want to keep it open
                  const focusedBackInNav = navRef.current.contains(
                    event.relatedTarget as Node
                  );

                  // Close the nav and search after tabbing out of the last nav item
                  if (navIsExpanded && !focusedBackInNav) {
                    closeNav();
                  }

                  if (searchIsExpanded && !focusedBackInNav) {
                    closeDesktopSearch();
                  }
                }}
              >
                {hasPaywallAccess ? "Give a Gift" : "Subscribe"}
              </a>
            </li>
          </ul>
        </div>
      </nav>
      {/*
       * We can disable the `no-static-element-interactions` warning here
       * because our overlay is only designed to capture outside
       * clicks, not to serve as a clickable element itself.
       *
       * We can disable the `click-events-have-key-events` warning because
       * we listen for an Escape keydown event in the `useEffect` hook
       */}
      {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={cx(styles.overlay, overlayOpen && styles.overlayOpen)}
        data-testid="overlay"
        onClick={() => {
          if (navIsExpanded) closeNav();
          if (searchIsExpanded) closeDesktopSearch();
        }}
      />
      {/* eslint-enable */}
    </>
  );
};

export default Nav;
